/**
 * Types for the Lighthouse desktop app bridge (`window.lighthouseDesktop`).
 *
 * MIRRORS lighthouse-desktop `src/shared/bridge.ts` and `src/shared/settings.ts`
 * (types only). Those files are the source of truth: keep this copy in sync
 * when the contract changes. New fields stay optional so an older UI deploy
 * keeps working against a newer shell and vice versa.
 */

// ── settings.ts ──────────────────────────────────────────────────────────

/** Desktop preferences: per-device, owned by the shell (product preferences live in the web app). */
export type StartPage = 'last' | 'home' | 'inbox' | 'agent' | 'calendar'

export interface ShortcutSettings {
    /** Opens the Quick Ask panel from anywhere. */
    quickAsk: string
    /** Region screenshot, then Quick Ask with the capture attached. */
    captureAsk: string
    /** Start or stop meeting notes. */
    toggleMeeting: string
    /** Bring the main window forward. */
    showApp: string
}

export interface DesktopSettings {
    general: {
        launchAtLogin: boolean
        /** Launch into the menu bar / tray without opening a window. */
        launchHidden: boolean
        showTrayIcon: boolean
        /** Windows/Linux: closing the window keeps Lighthouse running in the tray. */
        closeToTray: boolean
        startPage: StartPage
    }
    appearance: {
        /** macOS vibrancy and Windows 11 Mica behind the sidebar. */
        translucency: boolean
        /** Page zoom, 0.8 – 1.3. */
        zoom: number
        /** Reduce motion in the desktop's own windows. */
        reduceMotion: boolean
    }
    shortcuts: ShortcutSettings
    voice: {
        /** Preferred microphone (`MediaDeviceInfo.deviceId`). null = system default. */
        inputDeviceId: string | null
        inputDeviceLabel: string | null
        /** Play the start / stop chimes. */
        sounds: boolean
    }
    meetings: {
        /** Watch the calendar and offer to take notes when a call starts. */
        detectFromCalendar: boolean
        /** Minutes before a meeting to remind. 0 = at start. */
        remindMinutesBefore: number
        /** Record the other side of the call (system audio). */
        captureSystemAudio: boolean
        /** Show the "let everyone know" reminder when recording starts. */
        consentReminder: boolean
        /** Hand the finished notes to Lighthouse to update deals and draft follow-ups. */
        autoHandoff: boolean
    }
    notifications: {
        enabled: boolean
        agentUpdates: boolean
        approvals: boolean
        messages: boolean
        meetings: boolean
        sound: boolean
        badge: boolean
    }
    offline: {
        /** Keep the app shell on disk so Lighthouse opens offline and starts instantly. */
        cacheApp: boolean
    }
    privacy: {
        /** Keep Lighthouse's own windows out of screenshots and screen shares. */
        hideFromCapture: boolean
    }
    advanced: {
        /** Override the hosted app origin (staging, local dev). null = production. */
        appUrl: string | null
        updateChannel: 'stable' | 'beta'
        hardwareAcceleration: boolean
    }
    /** Window and session state the app restores; not shown in Settings. */
    state: {
        lastPath: string | null
        onboardedAt: string | null
    }
}

/** Deep partial used for settings patches sent over IPC. */
export type SettingsPatch = {
    [K in keyof DesktopSettings]?: Partial<DesktopSettings[K]>
}

// ── bridge.ts ────────────────────────────────────────────────────────────

export const BRIDGE_VERSION = 1

export type DesktopPlatform = 'darwin' | 'win32' | 'linux'

/** Geometry and state of the native window chrome, in CSS pixels. */
export interface ChromeMetrics {
    platform: DesktopPlatform
    /** Height of the unified toolbar strip the UI should reserve at the top. */
    titleBarHeight: number
    /** Width the macOS traffic lights occupy from the left edge (0 when hidden). */
    insetLeft: number
    /** Width the Windows caption buttons occupy from the right edge. */
    insetRight: number
    fullscreen: boolean
    focused: boolean
    maximized: boolean
    /** True when the window shows native material (vibrancy / Mica) behind transparent page areas. */
    translucent: boolean
    /** True while the shell has no network connection. */
    offline: boolean
    /** The navigation history the toolbar's back / forward buttons reflect. */
    canGoBack: boolean
    canGoForward: boolean
}

/** Commands the shell sends from the menu bar, tray, dock, jump list and shortcuts. */
export type DesktopCommand =
    | { type: 'navigate'; path: string }
    | { type: 'new-conversation' }
    | { type: 'open-search' }
    | { type: 'open-assistant' }
    | { type: 'toggle-sidebar' }
    | { type: 'compose-email' }
    | { type: 'new-task' }
    | { type: 'toggle-theme' }
    | { type: 'show-shortcuts' }
    | { type: 'focus-composer' }
    | { type: 'attach-files'; files: DesktopFile[] }
    /** The machine woke or the network came back: refetch anything stale. */
    | { type: 'resume' }
    /** A newer build of the web app is cached; reload when convenient. */
    | { type: 'web-update-ready' }
    /** The creator replied from a notification (macOS inline reply). Send it without opening the window. */
    | { type: 'reply'; sessionId: string; text: string; answer: boolean }
    /** Settings → Appearance in the desktop app changed the account's theme preference. */
    | { type: 'set-theme'; preference: 'light' | 'dark' | 'system' }

/** A file crossing the bridge. Bytes travel as ArrayBuffer; build `new File([data], name, { type })`. */
export interface DesktopFile {
    name: string
    type: string
    data: ArrayBuffer
}

export type AskSource = 'quick-ask' | 'capture' | 'meeting' | 'dock' | 'tray' | 'drop'

/** A prompt handed to the main window from Quick Ask, a capture or meeting notes. */
export interface AskRequest {
    id: string
    text: string
    files: DesktopFile[]
    /** `quick` maps to Instant, `standard` to Thinking. */
    tier: 'quick' | 'standard'
    /** `open` shows the conversation; `background` runs it quietly and notifies when it needs the user. */
    mode: 'open' | 'background'
    source: AskSource
    /** Optional title for the new conversation. */
    title?: string
}

export type AskOutcome = { ok: true; sessionId: string } | { ok: false; error: string }

export type NotificationKind = 'agent' | 'approval' | 'question' | 'message' | 'meeting' | 'general'

export interface DesktopNotification {
    /** Notifications with the same tag replace each other. */
    tag?: string
    title: string
    body: string
    /** App route to open when clicked, e.g. `/dashboard/conversations/abc`. */
    path?: string
    kind?: NotificationKind
    silent?: boolean
    /**
     * macOS: offer an inline Reply field. The text comes back as a `reply`
     * command. The shell only shows it when this UI reports `notification-reply`.
     */
    reply?: NotificationReply
    /**
     * Show it even while Lighthouse is the focused app. Set only after checking
     * the creator isn't already looking at what it's about.
     */
    whileFocused?: boolean
}

export interface NotificationReply {
    /** The conversation the reply goes to. */
    sessionId: string
    /** Send it as the answer to the agent's open question rather than a new message. */
    answer?: boolean
    placeholder?: string
}

/** The signed-in account, pushed by the UI on every Supabase auth change. */
export interface DesktopSession {
    accessToken: string
    /** Unix seconds. */
    expiresAt: number
    userId: string
    email?: string | null
    name?: string | null
    avatarUrl?: string | null
    /** Backend origin the UI talks to (`VITE_APP_BACKEND_URL`). */
    backendUrl: string
    /** Supabase project origin. */
    supabaseUrl?: string
}

export interface AppearanceState {
    /** The resolved theme the page is painting. */
    theme: 'light' | 'dark'
    preference: 'light' | 'dark' | 'system'
    /** The page background colour, used to paint the window before content loads. */
    page: string
}

export type UpdateStatus = 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'up-to-date' | 'error' | 'unsupported'

export interface UpdateState {
    status: UpdateStatus
    version?: string
    /** 0 – 1 while downloading. */
    progress?: number
    error?: string
    checkedAt?: number
}

export type MeetingStatus = 'idle' | 'starting' | 'recording' | 'paused' | 'finishing' | 'done' | 'error'

export interface MeetingState {
    status: MeetingStatus
    meetingId?: string
    title?: string
    startedAt?: number
    /** Milliseconds recorded, excluding pauses. */
    elapsedMs?: number
    /** True when the other side of the call is being captured. */
    systemAudio?: boolean
    /** Segments still waiting to upload (offline or slow network). */
    pendingUploads?: number
    error?: string
    /** App route of the finished notes. */
    path?: string
}

export interface UpcomingMeeting {
    id: string
    title: string
    startsAt: string
    endsAt?: string | null
    joinUrl?: string | null
    attendees?: string[]
}

export interface CaptureOptions {
    /** `region` lets the user drag a rectangle; `screen` grabs the display under the cursor. */
    mode?: 'region' | 'screen'
}

export interface SaveFileOptions {
    name: string
    data: ArrayBuffer | string
    type?: string
}

export type Unsubscribe = () => void

export interface LighthouseDesktopBridge {
    /** Desktop app version, e.g. `0.2.0`. */
    version: string
    bridgeVersion: number
    platform: DesktopPlatform
    arch: string
    /**
     * Chrome metrics at window creation, readable synchronously so the page can
     * lay out the title bar before first paint. Subscribe with `chrome.onChange` for live values.
     */
    initialChrome: ChromeMetrics

    /** The UI calls this once its desktop integration is mounted; the shell then stops its CSS fallback. */
    ready(info: { features: string[]; uiVersion?: string }): void

    chrome: {
        get(): Promise<ChromeMetrics>
        onChange(cb: (metrics: ChromeMetrics) => void): Unsubscribe
        setAppearance(state: AppearanceState): void
        /** Windows / Linux: pop the application menu (File, Edit, View…) at a point. */
        showAppMenu(point?: { x: number; y: number }): void
        goBack(): void
        goForward(): void
    }

    onCommand(cb: (command: DesktopCommand) => void): Unsubscribe

    setSession(session: DesktopSession | null): void

    ask: {
        /** Receive prompts from Quick Ask, captures and meeting notes. Reply with `complete`. */
        onRequest(cb: (request: AskRequest) => void): Unsubscribe
        complete(id: string, outcome: AskOutcome): void
        /** Open the Quick Ask panel, optionally prefilled. */
        open(prefill?: { text?: string; files?: DesktopFile[] }): void
    }

    notify(notification: DesktopNotification): void
    setBadge(count: number): void
    /** Bounce the dock icon / flash the taskbar button until the window is focused. */
    requestAttention(): void

    capture: {
        screenshot(options?: CaptureOptions): Promise<DesktopFile | null>
    }

    files: {
        /** Real path of a dropped or picked `File` ('' when it has none). */
        pathFor(file: File): string
        save(options: SaveFileOptions): Promise<{ saved: boolean; path?: string }>
        openExternal(url: string): void
    }

    auth: {
        /** Open an OAuth URL in the system browser. The result returns through `onDeepLink`. */
        openInBrowser(url: string): void
        onDeepLink(cb: (url: string) => void): Unsubscribe
    }

    meetings: {
        start(options?: { title?: string; eventId?: string }): Promise<void>
        stop(): Promise<void>
        state(): Promise<MeetingState>
        onState(cb: (state: MeetingState) => void): Unsubscribe
        /** The shell's view of the user's next meetings (from the calendar), for tray and reminders. */
        setUpcoming(meetings: UpcomingMeeting[]): void
    }

    settings: {
        open(section?: string): void
        get(): Promise<DesktopSettings>
        onChange(cb: (settings: DesktopSettings) => void): Unsubscribe
    }

    updates: {
        state(): Promise<UpdateState>
        onChange(cb: (state: UpdateState) => void): Unsubscribe
        check(): void
        install(): void
    }
}

declare global {
    interface Window {
        lighthouseDesktop?: LighthouseDesktopBridge
    }
}
